import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  blockUser,
  createStaffUser,
  fetchEnterprisesUsers,
  fetchOrganismesUsers,
  fetchParticularUsers,
  fetchUsersDirectory,
  fetchUsersList,
  unblockUser,
} from "../services/usersService";
import { resolveApiErrorMessage } from "../utils/resolveApiErrorMessage";

const USERS_LIST_QK = ["usersList"];
const USERS_DIRECTORY_QK = ["usersDirectory"];

const ORGANISMES_USERS_QK = ["users", "organismes"];
const ENTERPRISES_USERS_QK = ["users", "enterprises"];
const PARTICULARS_USERS_QK = ["users", "particulars"];

const USERS_STALE_TIME = 2 * 60 * 1000;

const baseQueryOptions = {
  staleTime: USERS_STALE_TIME,
  refetchOnWindowFocus: false,
};

export const useUsersList = () =>
  useQuery({
    queryKey: USERS_LIST_QK,
    queryFn: fetchUsersList,
    ...baseQueryOptions,
  });

export const useOrganismesUsers = () =>
  useQuery({
    queryKey: ORGANISMES_USERS_QK,
    queryFn: fetchOrganismesUsers,
    ...baseQueryOptions,
  });

export const useEnterprisesUsers = () =>
  useQuery({
    queryKey: ENTERPRISES_USERS_QK,
    queryFn: fetchEnterprisesUsers,
    ...baseQueryOptions,
  });

export const useParticularUsers = () =>
  useQuery({
    queryKey: PARTICULARS_USERS_QK,
    queryFn: fetchParticularUsers,
    ...baseQueryOptions,
  });

/**
 * Full directory used by the administration screen. Unlike `useUsersList`, it
 * includes blocked accounts — otherwise a blocked user would vanish from the
 * back-office and could never be unblocked.
 */
export const useUsersDirectory = (status) =>
  useQuery({
    queryKey: status ? [...USERS_DIRECTORY_QK, status] : USERS_DIRECTORY_QK,
    queryFn: () => fetchUsersDirectory(status),
    ...baseQueryOptions,
  });

// Blocking flips isActive, so every user-facing list goes stale at once.
const invalidateUserQueries = (qc) => {
  qc.invalidateQueries({ queryKey: USERS_DIRECTORY_QK });
  qc.invalidateQueries({ queryKey: USERS_LIST_QK });
  qc.invalidateQueries({ queryKey: ["users"] });
  qc.invalidateQueries({ queryKey: ["dashboard"] });
};

export const useBlockUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }) => blockUser(id, reason),
    onSuccess: () => {
      invalidateUserQueries(qc);
      toast.success("Compte bloqué. L'utilisateur a été notifié par e-mail.", {
        autoClose: 4000,
      });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Blocage impossible"), {
        autoClose: 5000,
      }),
  });
};

export const useUnblockUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => unblockUser(id),
    onSuccess: () => {
      invalidateUserQueries(qc);
      toast.success("Compte réactivé. L'utilisateur a été notifié par e-mail.", {
        autoClose: 4000,
      });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Réactivation impossible"), {
        autoClose: 5000,
      }),
  });
};

export const useCreateStaffUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createStaffUser,
    onSuccess: () => {
      invalidateUserQueries(qc);
      toast.success("Compte interne créé", { autoClose: 3000 });
    },
    onError: (err) =>
      toast.error(resolveApiErrorMessage(err, "Création impossible"), {
        autoClose: 5000,
      }),
  });
};
